import React from "react";

import { TopMenu } from "../top-menu";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useTextZoneAlignment } from "../../bottom-text-menus/text-alignment-menu/text-alignment-panel-container";
import { useAllowedTextZoneAlignments } from "../../../hooks/use-allowed-textzone-alignments";
import { IconName } from "../../icon";
import { useSelectedZone } from "../../../hooks/useSelection";
import { Tooltip } from "../../tooltip/tooltip";
import { useLabels } from "../../../hooks/useLabels";
import { labels } from "./labels";

const HORIZONTAL_ALIGNMENT_ORDER = [
	eXpress.core.TextAlignment.Near,
	eXpress.core.TextAlignment.Center,
	eXpress.core.TextAlignment.Far,
] as const;

const HORIZONTAL_ALIGNMENT_ICONS: Record<eXpress.core.TextAlignment, IconName> = {
	[eXpress.core.TextAlignment.Near]: "text-align-left",
	[eXpress.core.TextAlignment.Center]: "text-align-center",
	[eXpress.core.TextAlignment.Far]: "text-align-right",
};

const VERTICAL_ALIGNMENT_ORDER = [
	eXpress.core.TextAlignment.Near,
	eXpress.core.TextAlignment.Center,
	eXpress.core.TextAlignment.Far,
] as const;

const VERTICAL_ALIGNMENT_ICONS: Record<eXpress.core.TextAlignment, IconName> = {
	[eXpress.core.TextAlignment.Near]: "text-valign-top",
	[eXpress.core.TextAlignment.Center]: "text-valign-middle",
	[eXpress.core.TextAlignment.Far]: "text-valign-bottom",
};

interface TextAlignmentButtonProps {
	direction: "horizontal" | "vertical";
}

export function TextAlignmentButton({ direction }: TextAlignmentButtonProps) {
	const zone = useSelectedZone();
	const textZone = eXpress.core.isAnyTextZone(zone) ? zone : undefined;
	const [horizontalAlignment, verticalAlignment] = useTextZoneAlignment(textZone);
	const allowedAlignments = useAllowedTextZoneAlignments();
	const textDashboard = useTextZoneDashboard();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();

	const isHorizontal = direction === "horizontal";
	const alignmentIcons = isHorizontal ? HORIZONTAL_ALIGNMENT_ICONS : VERTICAL_ALIGNMENT_ICONS;
	const currentAlignment = isHorizontal ? horizontalAlignment : verticalAlignment;

	const [[horizontalAlignmentTooltipText, verticalAlignmentTooltipText]] = useLabels([
		labels.horizontalAlignmentTooltip,
		labels.verticalAlignmentTooltip,
	]);

	const allowedHorizontalOrder = HORIZONTAL_ALIGNMENT_ORDER.filter(
		(horizontalAlignments) =>
			({
				[eXpress.core.TextAlignment.Near]:
					allowedAlignments.canSetHorizontalAlignmentToNear,
				[eXpress.core.TextAlignment.Center]:
					allowedAlignments.canSetHorizontalAlignmentToCenter,
				[eXpress.core.TextAlignment.Far]: allowedAlignments.canSetHorizontalAlignmentToFar,
			})[horizontalAlignments],
	);

	const allowedVerticalOrder = VERTICAL_ALIGNMENT_ORDER.filter(
		(verticalAlignments) =>
			({
				[eXpress.core.TextAlignment.Near]: allowedAlignments.canSetVerticalAlignmentToNear,
				[eXpress.core.TextAlignment.Center]:
					allowedAlignments.canSetVerticalAlignmentToCenter,
				[eXpress.core.TextAlignment.Far]: allowedAlignments.canSetVerticalAlignmentToFar,
			})[verticalAlignments],
	);

	const allowedOrder = isHorizontal ? allowedHorizontalOrder : allowedVerticalOrder;

	const handleClick = () => {
		if (!textZone || allowedOrder.length === 0) return;
		const currentIndex = allowedOrder.indexOf(currentAlignment);
		const next = allowedOrder[(currentIndex + 1) % allowedOrder.length];
		if (isHorizontal) {
			busyBlocker
				.during(textDashboard.setHorizontalAlignmentAsync(textZone, next))
				.catch(createErrorHandler());
		} else {
			busyBlocker
				.during(textDashboard.setVerticalAlignmentAsync(textZone, next))
				.catch(createErrorHandler());
		}
	};

	return (
		<Tooltip
			text={isHorizontal ? horizontalAlignmentTooltipText : verticalAlignmentTooltipText}
		>
			<TopMenu.Icon iconName={alignmentIcons[currentAlignment]} onClick={handleClick} />
		</Tooltip>
	);
}
